import type { EmployeeProjectModel } from '../services/employeeProjectModelService';

function parseUtcDayMs(iso: string | undefined | null): number | null {
  if (!iso || typeof iso !== 'string') return null;
  const day = iso.split('T')[0];
  const parts = day.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [y, m, d] = parts;
  return Date.UTC(y, m - 1, d);
}

function rangeForModel(model: EmployeeProjectModel): { start: number; end: number } | null {
  const start = parseUtcDayMs(model.start_date);
  if (start === null) return null;
  const endRaw = parseUtcDayMs(model.end_date);
  const end = endRaw === null ? start : endRaw;
  if (end < start) return { start, end: start };
  return { start, end };
}

function rangesOverlap(
  a: { start: number; end: number },
  b: { start: number; end: number }
): boolean {
  return a.start <= b.end && b.start <= a.end;
}

function normalizeWorkType(workType: string | undefined | null): string {
  return (workType ?? '').trim().toLowerCase();
}

function isOfficeSitePair(a: EmployeeProjectModel, b: EmployeeProjectModel): boolean {
  const wa = normalizeWorkType(a.work_type);
  const wb = normalizeWorkType(b.work_type);
  return (wa === 'office' && wb === 'site') || (wa === 'site' && wb === 'office');
}

function normalizeOrder(order: string | undefined | null): string {
  return (order ?? '').trim();
}

function ordersDiffer(a: EmployeeProjectModel, b: EmployeeProjectModel): boolean {
  return normalizeOrder(a.order) !== normalizeOrder(b.order);
}

function ratesConflict(a: EmployeeProjectModel, b: EmployeeProjectModel): boolean {
  if (normalizeWorkType(a.work_type) !== normalizeWorkType(b.work_type)) return true;
  return a.sales_rate !== b.sales_rate;
}

export function findAmbiguousModelIds(models: EmployeeProjectModel[]): Set<string> {
  const ambiguous = new Set<string>();
  const n = models.length;
  for (let i = 0; i < n; i++) {
    const a = models[i];
    const ra = rangeForModel(a);
    if (!ra) continue;
    for (let j = i + 1; j < n; j++) {
      const b = models[j];
      if (a.employee_id !== b.employee_id) continue;
      const rb = rangeForModel(b);
      if (!rb) continue;
      if (ordersDiffer(a, b)) continue;
      if (isOfficeSitePair(a, b)) continue;
      if (!rangesOverlap(ra, rb)) continue;
      if (!ratesConflict(a, b)) continue;
      ambiguous.add(a.id);
      ambiguous.add(b.id);
    }
  }
  return ambiguous;
}
